import React, { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import PageHero from "@/components/PageHero";
import { Field, Input, Textarea, Select } from "@/components/Field";
import Seo from "@/components/Seo";

const PROJECT_TYPES = ["Website", "Online Shop", "Landing Page", "Mobile App (Android)", "Mobile App (iOS)", "Cross-Platform App", "Web Application", "Business Software", "Admin Panel", "Customer Portal", "Booking System", "AI Integration", "Website Redesign", "Maintenance", "Other"];
const PLATFORMS = ["Web", "Android", "iOS", "Cross-Platform", "Tablet", "Desktop"];
const BUDGETS = ["< 2.000€", "2.000€ - 5.000€", "5.000€ - 10.000€", "10.000€ - 25.000€", "> 25.000€"];

export default function Quote() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    name: user?.name || "", company: user?.company || "", email: user?.email || "",
    phone: "", project_type: PROJECT_TYPES[0], platform: "Web", budget: BUDGETS[1],
    deadline: "", features: "", description: "", existing_url: "", gdpr: false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.gdpr) { toast.error(lang === "de" ? "Bitte GDPR akzeptieren." : "Please accept GDPR."); return; }
    setLoading(true);
    try {
      await api.post("/quotes", { ...f, customer_id: user?.id, customer_name: f.name, customer_language: lang });
      setDone(true);
      toast.success(t("quote.success"));
    } catch (err) { toast.error(apiErr(err)); }
    setLoading(false);
  };
  if (done) return (
    <div className="container-x py-28 text-center max-w-lg mx-auto">
      <CheckCircle2 size={56} className="text-primary mx-auto mb-6" />
      <h1 className="font-display text-3xl font-black mb-3">{t("quote.success")}</h1>
      <p className="text-muted-foreground">{lang === "de" ? "Wir melden uns innerhalb von 24 Stunden bei Ihnen." : "We'll get back to you within 24 hours."}</p>
    </div>
  );
  return (
    <div>
      <Seo title={t("quote.title")} description={t("quote.desc")} />
      <PageHero eyebrow={t("nav.quote")} title={t("quote.title")} subtitle={t("quote.desc")} />
      <section className="container-x py-14 max-w-3xl">
        <form onSubmit={submit} className="surface p-8 space-y-5" data-testid="quote-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("common.name")}><Input testid="quote-name" required value={f.name} onChange={set("name")} /></Field>
            <Field label={t("auth.company")}><Input testid="quote-company" value={f.company} onChange={set("company")} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("common.email")}><Input testid="quote-email" type="email" required value={f.email} onChange={set("email")} /></Field>
            <Field label={t("auth.phone")}><Input testid="quote-phone" value={f.phone} onChange={set("phone")} /></Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label={t("quote.projectType")}><Select testid="quote-type" value={f.project_type} onChange={set("project_type")}>{PROJECT_TYPES.map((x) => <option key={x}>{x}</option>)}</Select></Field>
            <Field label={t("quote.platform")}><Select testid="quote-platform" value={f.platform} onChange={set("platform")}>{PLATFORMS.map((x) => <option key={x}>{x}</option>)}</Select></Field>
            <Field label={t("quote.budget")}><Select testid="quote-budget" value={f.budget} onChange={set("budget")}>{BUDGETS.map((x) => <option key={x}>{x}</option>)}</Select></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("quote.deadline")}><Input testid="quote-deadline" type="date" value={f.deadline} onChange={set("deadline")} /></Field>
            <Field label={lang === "de" ? "Bestehende Website" : "Existing website"}><Input testid="quote-url" value={f.existing_url} onChange={set("existing_url")} placeholder="https://" /></Field>
          </div>
          <Field label={t("quote.features")}><Textarea testid="quote-features" value={f.features} onChange={set("features")} placeholder={lang === "de" ? "z.B. Login, Zahlungen, Buchungen…" : "e.g. login, payments, bookings…"} /></Field>
          <Field label={t("quote.description")}><Textarea testid="quote-description" required value={f.description} onChange={set("description")} /></Field>
          <label className="flex items-center gap-2.5 text-sm">
            <input type="checkbox" checked={f.gdpr} onChange={set("gdpr")} data-testid="quote-gdpr" className="h-4 w-4 accent-[hsl(21,100%,50%)]" /> {t("quote.gdpr")}
          </label>
          <button className="btn-primary w-full" disabled={loading} data-testid="quote-submit">{loading ? "…" : t("quote.submit")}</button>
        </form>
      </section>
    </div>
  );
}
